import type { InAppNotification } from "@/features/notifications/api/inAppNotificationsApi";

export type InAppDayGroupLabel = "Today" | "Yesterday" | "Earlier";

export interface InAppDayGroup {
  label: InAppDayGroupLabel;
  items: InAppNotification[];
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function dayKey(d: Date, timeZone: string | undefined): string {
  if (timeZone) {
    try {
      return new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(d);
    } catch {
      /* fall through */
    }
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function labelForKey(key: string, todayKey: string, yesterdayKey: string): InAppDayGroupLabel {
  if (key === todayKey) return "Today";
  if (key === yesterdayKey) return "Yesterday";
  return "Earlier";
}

/** Bucket in-app rows by calendar day (tenant TZ when provided). */
export function groupInAppByDay(
  items: InAppNotification[],
  timeZone?: string,
): InAppDayGroup[] {
  const now = new Date();
  const todayKey = dayKey(now, timeZone);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = dayKey(yesterday, timeZone);

  const buckets = new Map<InAppDayGroupLabel, InAppNotification[]>();
  const order: InAppDayGroupLabel[] = [];

  for (const item of items) {
    const sent = item.sent_at ? new Date(item.sent_at) : null;
    const key = sent && !Number.isNaN(sent.getTime()) ? dayKey(sent, timeZone) : todayKey;
    const label = labelForKey(key, todayKey, yesterdayKey);
    if (!buckets.has(label)) {
      buckets.set(label, []);
      order.push(label);
    }
    buckets.get(label)!.push(item);
  }

  return order.map((label) => ({ label, items: buckets.get(label)! }));
}

export { startOfDay };
